import { serviceFaqs, type FaqItem } from './faq'

export type ServiceOffering = {
  title: string
  body: string
}

export type ServicePageContent = {
  slug: string
  path: string
  eyebrow: string
  headline: string
  headlineAccent: string
  lede: string
  seoTitle: string
  seoDescription: string
  intro: string[]
  offerings: ServiceOffering[]
  faqs: FaqItem[]
}

export const servicePages: ServicePageContent[] = [
  {
    slug: 'web-design',
    path: '/services/web-design',
    eyebrow: 'Web design',
    headline: 'Marketing sites',
    headlineAccent: 'that convert.',
    lede: 'Clear structure, fast pages, and a brand that looks finished - built for founder-led teams in Vancouver and across Canada.',
    seoTitle: 'Vancouver Web Design for Service Businesses | Elementary Creative',
    seoDescription:
      'Web design and development for Vancouver and Canadian service businesses. Marketing sites with clear structure, on-page SEO, and production hosting.',
    intro: [
      'Most small businesses do not need a 40-page brochure. They need a site that explains the offer, shows proof, and makes it easy to call, book, or write.',
      'We design and build marketing sites in React, then host them so you are not stuck with a theme you cannot change. Copy, layout, and technical SEO are part of the same pass - not a later add-on.',
    ],
    offerings: [
      {
        title: 'Marketing & brochure sites',
        body: 'Home, services, work, and contact - scoped so a visitor can decide in one sitting.',
      },
      {
        title: 'Content structure',
        body: 'Wireframes and page outlines before pixels, so the site matches how you sell.',
      },
      {
        title: 'On-page SEO',
        body: 'Titles, headings, internal links, and sitemap work that search engines can actually use.',
      },
      {
        title: 'Launch & hosting',
        body: 'Production deploys, redirects, analytics, and a handoff you can maintain.',
      },
    ],
    faqs: serviceFaqs,
  },
  {
    slug: 'custom-software',
    path: '/services/custom-software',
    eyebrow: 'Custom software',
    headline: 'Tools that match',
    headlineAccent: 'how you work.',
    lede: 'POS, booking, CRM, and internal dashboards when Square, spreadsheets, or a generic app get in the way.',
    seoTitle: 'Custom Software, POS & Booking Systems | Elementary Creative',
    seoDescription:
      'Custom POS, booking, CRM, and internal tools for Canadian businesses. Built when off-the-shelf software does not match your workflow.',
    intro: [
      'Off-the-shelf software is fine until the workaround becomes the job. We build line-of-business tools around the workflow you already have - appointments, tickets, inventory, or client records.',
      'These are production systems, not demos: auth, roles, payments where needed, and an admin your staff can use without a training week.',
    ],
    offerings: [
      {
        title: 'POS & retail',
        body: 'Checkout, tickets, and reporting for shops that outgrow a generic register.',
      },
      {
        title: 'Booking & scheduling',
        body: 'Staff calendars, services, deposits, and reminders - without a maze of third-party widgets.',
      },
      {
        title: 'CRM & client portals',
        body: 'Records, orders, and status in one place so the team stops hunting through email.',
      },
      {
        title: 'Internal dashboards',
        body: 'Ops views for owners who need numbers without exporting a spreadsheet every Friday.',
      },
    ],
    faqs: serviceFaqs,
  },
  {
    slug: 'graphic-design',
    path: '/services/graphic-design',
    eyebrow: 'Brand & graphic design',
    headline: 'Identity that',
    headlineAccent: 'holds up in use.',
    lede: 'Logos, visual systems, and marketing assets designed to sit next to the website and product - not as a one-off PDF.',
    seoTitle: 'Graphic Design & Brand Packages | Elementary Creative',
    seoDescription:
      'Brand and graphic design for Vancouver businesses - logos, visual identity, and marketing assets paired with web and product work.',
    intro: [
      'Brand work here is practical. We set type, color, and a mark you can use on a van wrap, a booking screen, and a homepage without redrawing it each time.',
      'Packages pair with web and product work when you need both. If you already have a site, we can still deliver identity and campaign assets on their own.',
    ],
    offerings: [
      {
        title: 'Logo & mark',
        body: 'A primary mark and simple lockups that survive small sizes and one-color print.',
      },
      {
        title: 'Visual identity',
        body: 'Type, color, and spacing rules so a contractor can make a flyer that still looks like you.',
      },
      {
        title: 'Marketing assets',
        body: 'Launch graphics, social covers, and one-pagers that match the site.',
      },
      {
        title: 'Guidelines',
        body: 'A short guide - not a 60-page brand book nobody opens.',
      },
    ],
    faqs: serviceFaqs,
  },
]

export function getServiceBySlug(slug: string | undefined) {
  if (!slug) return undefined
  return servicePages.find((page) => page.slug === slug)
}
